import { motion } from "framer-motion";
import { Users, Languages, Network, Check } from "lucide-react";
import { regions } from "@/data/siteData";

const highlights = [
  {
    n: "25+ Cr",
    l: "Digital Audience",
    sub: "Across Telugu, Tamil, Kannada & Malayalam",
    icon: Users,
    tid: "25",
  },
  {
    n: "04",
    l: "Major Languages",
    sub: "First-language fluency, every market",
    icon: Languages,
    tid: "4",
  },
  {
    n: "Deep",
    l: "Creator Networks",
    sub: "1000+ creators on retainer-grade access",
    icon: Network,
    tid: "deep",
  },
];

const bullets = [
  "25+ crore digital audience",
  "4 major languages",
  "Deep creator networks",
];

export default function RegionalDominance() {
  return (
    <section
      id="regions"
      data-testid="regional-dominance-section"
      className="relative py-16 md:py-20 px-6 md:px-10 surface-2 overflow-hidden"
    >
      {/* Background grid texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04] dark:opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(30,58,138,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(30,58,138,0.6) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 80%)",
        }}
      />
      {/* Soft blue glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-20 right-0 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-40 dark:opacity-30"
        style={{
          background:
            "radial-gradient(circle, rgba(37,99,235,0.18) 0%, transparent 65%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl">
        {/* TWO-COLUMN MAIN AREA */}
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-14 items-start">
          {/* LEFT: heading + bullets + statement */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-400/20 px-3 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB] anim-pulse-glow" />
              <span className="text-[11px] tracking-[0.26em] uppercase font-semibold text-[#2563EB] dark:text-blue-300">
                Regional Dominance
              </span>
            </span>

            <h2
              data-testid="regional-headline"
              className="mt-5 font-heading font-bold text-4xl sm:text-5xl lg:text-[56px] tracking-tight leading-[1.02] text-slate-900 dark:text-white"
            >
              Built for{" "}
              <span className="gradient-text">South India.</span>
            </h2>

            <ul className="mt-7 space-y-3">
              {bullets.map((b, i) => (
                <li
                  key={b}
                  className="flex items-center gap-3 text-[16px] md:text-[17px] font-medium text-slate-700 dark:text-slate-200"
                >
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#2563EB]/10 dark:bg-[#2563EB]/25 text-[#2563EB] dark:text-blue-300">
                    <Check className="w-3 h-3" strokeWidth={3.5} />
                  </span>
                  <span>
                    <span className="font-semibold text-slate-900 dark:text-white">
                      {b.split(" ")[0]}
                    </span>{" "}
                    {b.split(" ").slice(1).join(" ")}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-8 relative pl-5 border-l-2 border-[#2563EB]/40 dark:border-blue-400/30">
              <p className="font-serif-italic text-xl md:text-2xl text-slate-800 dark:text-white leading-snug font-medium">
                We don't just reach people —{" "}
                <span className="text-[#2563EB] dark:text-blue-300 font-semibold">
                  we connect with them.
                </span>
              </p>
            </div>
          </motion.div>

          {/* RIGHT: vertically stacked stat cards */}
          <div className="space-y-3 lg:space-y-3.5">
            {highlights.map((h, i) => {
              const Icon = h.icon;
              return (
                <motion.div
                  key={h.l}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.55, delay: 0.1 + i * 0.08 }}
                  data-testid={`regional-highlight-${h.tid}`}
                  className="group relative overflow-hidden rounded-2xl bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 px-5 py-4 md:px-6 md:py-5 shadow-[0_10px_30px_-20px_rgba(15,23,42,0.25)] hover:border-[#2563EB]/50 hover:-translate-y-1 hover:shadow-[0_24px_60px_-25px_rgba(37,99,235,0.45)] transition-all duration-400"
                >
                  <div className="flex items-center gap-5">
                    <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-500/15 dark:to-blue-500/5 border border-blue-100 dark:border-blue-400/20 flex items-center justify-center text-[#2563EB] dark:text-blue-300">
                      <Icon className="w-5 h-5" strokeWidth={2} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-extrabold text-3xl md:text-[34px] leading-none tracking-tight text-slate-900 dark:text-white">
                        {h.n}
                      </div>
                      <div className="mt-1.5 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#2563EB] dark:text-blue-300">
                        {h.l}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 dark:border-white/[0.06] text-[13px] font-medium text-slate-600 dark:text-slate-300">
                    {h.sub}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* LANGUAGE CARDS — 4-col grid */}
        <div className="mt-16 md:mt-20">
          <div className="flex items-end justify-between mb-6 gap-4">
            <div>
              <span className="text-[11px] tracking-[0.26em] uppercase font-semibold text-slate-400 dark:text-slate-500">
                By Language
              </span>
              <h3 className="mt-2 font-heading font-bold text-2xl md:text-[28px] tracking-tight text-slate-900 dark:text-white">
                Native fluency, every market.
              </h3>
            </div>
            <div className="hidden md:flex items-center gap-2 text-[12px] font-medium text-slate-500 dark:text-slate-400">
              <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB]" />
              Live monthly reach
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {regions.map((r, i) => (
              <motion.div
                key={r.lang}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                data-testid={`region-card-${r.lang.toLowerCase()}`}
                className="group relative overflow-hidden rounded-2xl p-6 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_6px_20px_rgba(0,0,0,0.04)] dark:hover:shadow-[0_28px_60px_-22px_rgba(37,99,235,0.4)]"
                style={{
                  backgroundColor: "#F5F7FB",
                  border: "1px solid #E2E8F0",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#FFFFFF";
                  e.currentTarget.style.borderColor = "rgba(59,130,246,0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#F5F7FB";
                  e.currentTarget.style.borderColor = "#E2E8F0";
                }}
              >
                {/* hover glow */}
                <div
                  aria-hidden
                  className="absolute -top-24 -right-16 w-56 h-56 rounded-full bg-blue-500/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                />
                {/* subtle tint background */}
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(59,130,246,0.035) 0%, transparent 60%)",
                  }}
                />

                {/* TOP: index + region */}
                <div className="relative flex items-center justify-between">
                  <span
                    className="text-[11px] tracking-[0.24em] uppercase font-semibold tabular-nums"
                    style={{ color: "#3B82F6" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    className="text-[12px] font-medium"
                    style={{ color: "#64748B" }}
                  >
                    {r.note}
                  </span>
                </div>

                {/* MIDDLE: language name — hollow outline style */}
                <div
                  className="relative mt-7 font-heading font-extrabold text-[40px] lg:text-[36px] xl:text-[42px] tracking-tight leading-none transition-colors duration-300"
                  style={{
                    color: "transparent",
                    WebkitTextStroke: "1.5px #1E293B",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.WebkitTextStroke = "1.5px #3B82F6";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.WebkitTextStroke = "1.5px #1E293B";
                  }}
                >
                  {r.lang}
                </div>

                {/* BOTTOM: monthly reach highlighted */}
                <div
                  className="relative mt-6 pt-4"
                  style={{ borderTop: "1px solid #E2E8F0" }}
                >
                  <div
                    className="text-[10.5px] uppercase font-semibold"
                    style={{ color: "#94A3B8", letterSpacing: "0.24em" }}
                  >
                    Monthly Reach
                  </div>
                  <div
                    className="mt-1.5 font-heading font-bold text-[18px] md:text-[19px] tracking-tight"
                    style={{ color: "#1E293B" }}
                  >
                    {r.stat}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
